/** Decodes a data URL or bare base64 payload into an image element. */
async function loadBase64Image(base64Image: string): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = (err) => reject(err)
    image.src = base64Image.startsWith('data:') ? base64Image : `data:image/png;base64,${base64Image}`
  })
}

/**
 * The largest centered rectangle of `targetRatio` that fits inside a
 * `srcWidth` x `srcHeight` image. Kept separate from the canvas work so the
 * framing math is testable without a DOM.
 */
export function computeCenterCropBox(
  srcWidth: number,
  srcHeight: number,
  targetRatio: number,
): { x: number; y: number; width: number; height: number } {
  const srcRatio = srcWidth / srcHeight
  let width: number
  let height: number
  if (srcRatio > targetRatio) {
    // Source is wider than the target frame — trim the sides.
    height = srcHeight
    width = Math.round(srcHeight * targetRatio)
  } else {
    width = srcWidth
    height = Math.round(srcWidth / targetRatio)
  }
  // Rounding can push the box a pixel past the source on extreme ratios.
  width = Math.min(width, srcWidth)
  height = Math.min(height, srcHeight)
  return {
    x: Math.round((srcWidth - width) / 2),
    y: Math.round((srcHeight - height) / 2),
    width,
    height,
  }
}

/**
 * Center-crops a base64 image to `targetRatio`, keeping the source's own
 * proportions and trimming the overflow — never stretching it to fit.
 *
 * Generation models only honour a requested aspect ratio approximately, so a
 * result that has to land in a frame sized from that ratio goes through this
 * first, otherwise the frame letterboxes or distorts it.
 *
 * @param tolerance Skip the crop when the source ratio is already within this
 *   absolute distance of the target. Defaults to `0` — crop unless the ratio
 *   is already exact.
 */
export async function centerCropToAspectRatio(
  base64Image: string,
  targetRatio: number,
  tolerance = 0,
): Promise<string> {
  if (!(targetRatio > 0)) return base64Image

  const img = await loadBase64Image(base64Image)

  const srcW = img.naturalWidth
  const srcH = img.naturalHeight
  if (!srcW || !srcH) return base64Image
  if (Math.abs(srcW / srcH - targetRatio) <= tolerance) return base64Image

  const box = computeCenterCropBox(srcW, srcH, targetRatio)
  // Nothing to trim — re-encoding would only cost quality.
  if (box.width === srcW && box.height === srcH) return base64Image

  const canvas = document.createElement('canvas')
  canvas.width = box.width
  canvas.height = box.height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, box.x, box.y, box.width, box.height, 0, 0, box.width, box.height)
  return canvas.toDataURL('image/png')
}

/**
 * Center-crops a base64 image to `targetWidth` x `targetHeight`'s ratio and
 * scales it to exactly those pixels — cover framing in a single draw, so the
 * image is enlarged (or shrunk) by one factor on both axes and the overflow is
 * trimmed. Never stretches.
 *
 * Used when a generated result has to match a known canvas exactly, e.g. the
 * "Original size" pick, where the card frame is the source photo's own pixels.
 */
export async function coverResizeToSize(
  base64Image: string,
  targetWidth: number,
  targetHeight: number,
): Promise<string> {
  const width = Math.round(targetWidth)
  const height = Math.round(targetHeight)
  if (!(width > 0) || !(height > 0)) return base64Image

  const img = await loadBase64Image(base64Image)
  const srcW = img.naturalWidth
  const srcH = img.naturalHeight
  if (!srcW || !srcH) return base64Image
  // Already the exact canvas — re-encoding would only cost quality.
  if (srcW === width && srcH === height) return base64Image

  const box = computeCenterCropBox(srcW, srcH, width / height)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, box.x, box.y, box.width, box.height, 0, 0, width, height)
  return canvas.toDataURL('image/png')
}
